/**
 * 用户管理页面
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { formatInDisplayTz } from '@/lib/time';
import {
  UserIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { userApi } from '@/lib/api';

type StatusFilter = 'all' | 'active' | 'deleted';

// 引用全局类型定义
import { User } from '@/types';

export default function UserManagement() {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [restoreReason, setRestoreReason] = useState('');

  const pageSize = 20;

  // 获取用户列表
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['admin-users', currentPage, statusFilter, searchQuery],
    queryFn: () => userApi.getUsers({
      page: currentPage,
      limit: pageSize,
      status: statusFilter,
      search: searchQuery || undefined,
    }),
  });

  const users = usersData?.users ?? [];
  const pagination = usersData?.pagination;

  // 软删除用户
  const deleteUserMutation = useMutation({
    mutationFn: (params: { userId: string; reason: string }) =>
      userApi.deleteUser(params.userId, params.reason),
    onSuccess: () => {
      toast.success('用户已成功注销');
      setShowDeleteModal(false);
      setSelectedUser(null);
      setDeleteReason('');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.error;
      const message = error?.response?.data?.message || '注销用户失败';

      if (status === 401) {
        toast.error('认证失败：管理员权限不足');
      } else if (status === 403) {
        toast.error('权限不足：无法执行此操作');
      } else if (status === 404) {
        toast.error('用户不存在或已被删除');
      } else if (status === 409) {
        if (errorCode === 'invalid_request') {
          // 业务规则限制
          toast.error(`业务规则限制：${message}`);
        } else {
          toast.error('操作冲突：用户状态已发生变化');
        }
      } else if (status === 400) {
        toast.error(`请求错误：${message}`);
      } else {
        toast.error(`注销失败：${message}`);
      }
    },
  });

  // 恢复用户
  const restoreUserMutation = useMutation({
    mutationFn: (params: { userId: string; reason: string }) =>
      userApi.restoreUser(params.userId, params.reason),
    onSuccess: () => {
      toast.success('用户已成功恢复');
      setShowRestoreModal(false);
      setSelectedUser(null);
      setRestoreReason('');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const errorCode = error?.response?.data?.error;
      const message = error?.response?.data?.message || '恢复用户失败';

      if (status === 401) {
        toast.error('认证失败：管理员权限不足');
      } else if (status === 403) {
        toast.error('权限不足：无法执行此操作');
      } else if (status === 404) {
        toast.error('用户不存在或未被删除');
      } else if (status === 409) {
        toast.error('操作冲突：用户状态已发生变化');
      } else if (status === 400) {
        toast.error(`请求错误：${message}`);
      } else {
        toast.error(`恢复失败：${message}`);
      }
    },
  });

  const resetPagination = () => {
    setCurrentPage(1);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDeleteReason('');
    setShowDeleteModal(true);
  };

  const handleRestoreUser = (user: User) => {
    setSelectedUser(user);
    setRestoreReason('');
    setShowRestoreModal(true);
  };

  const validateReason = (reason: string): boolean => {
    const trimmed = reason.trim();
    return trimmed.length >= 5 && trimmed.length <= 200;
  };

  const confirmDelete = () => {
    if (!selectedUser) {
      toast.error('请选择要删除的用户');
      return;
    }

    if (!validateReason(deleteReason)) {
      toast.error('注销原因长度必须在5-200字符之间');
      return;
    }

    deleteUserMutation.mutate({
      userId: selectedUser.id,
      reason: deleteReason.trim(),
    });
  };

  const confirmRestore = () => {
    if (!selectedUser) {
      toast.error('请选择要恢复的用户');
      return;
    }

    if (!validateReason(restoreReason)) {
      toast.error('恢复原因长度必须在5-200字符之间');
      return;
    }

    restoreUserMutation.mutate({
      userId: selectedUser.id,
      reason: restoreReason.trim(),
    });
  };

  const canDeleteUser = (user: User) => {
    // 只有活跃用户可以被删除
    return user.status === 'active';
  };

  const getDeleteButtonTooltip = (user: User) => {
    if (user.status !== 'active') {
      return '用户已注销，无法重复操作';
    }
    return '注销用户账号';
  };

  const getStatusBadge = (user: User) => {
    if (user.status === 'deleted') {
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
          已注销
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
        正常
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">用户管理</h1>
        <p className="text-gray-600">管理系统用户账号，支持软删除和恢复操作</p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="搜索用户昵称..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  resetPagination();
                }}
                className="form-input pl-10"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                resetPagination();
              }}
              className="form-select"
            >
              <option value="active">正常用户</option>
              <option value="deleted">已注销用户</option>
              <option value="all">全部用户</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : users.length === 0 ? (
          <div className="py-12 text-center">
            <UserIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">没有找到用户</h3>
            <p className="text-gray-600">当前条件下没有用户数据</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      用户信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      活动统计
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      注册时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className={`hover:bg-gray-50 ${user.status === 'deleted' ? 'opacity-60' : ''}`}
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{user.nickname}</div>
                          <div className="text-sm text-gray-500">
                            {user.channel && `渠道: ${user.channel}`}
                            {user.region && ` · 地区: ${user.region}`}
                          </div>
                          {user.deleted_reason && (
                            <div className="text-sm text-red-600 mt-1">
                              注销原因: {user.deleted_reason}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {getStatusBadge(user)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        <div>答题: {user.total_attempts} 次</div>
                        <div>获奖: {user.awards_count} 次</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                        <div>{formatInDisplayTz(user.created_at, 'yyyy-MM-dd HH:mm')}</div>

                        {user.deleted_at && (
                          <div className="text-red-600">
                            注销: {formatInDisplayTz(user.deleted_at, 'yyyy-MM-dd HH:mm')}

                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <div className="flex gap-3">
                          {user.status === 'active' ? (
                            <button
                              onClick={() => handleDeleteUser(user)}
                              disabled={!canDeleteUser(user)}
                              className={`flex items-center gap-1 ${
                                canDeleteUser(user)
                                  ? 'text-red-600 hover:text-red-800'
                                  : 'text-gray-400 cursor-not-allowed'
                              }`}
                              title={getDeleteButtonTooltip(user)}
                            >
                              <TrashIcon className="h-4 w-4" />
                              注销
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRestoreUser(user)}
                              className="flex items-center gap-1 text-green-600 hover:text-green-800"
                              title="恢复用户账号"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              恢复
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="border-t border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    显示 {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total)} 条，共 {pagination.total} 条
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage <= 1}
                      className="btn btn-secondary text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      上一页
                    </button>
                    <span className="px-3 py-1 text-sm">
                      {currentPage} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                      disabled={currentPage >= pagination.totalPages}
                      className="btn btn-secondary text-sm disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      下一页
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900">注销用户账号</h3>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                您即将注销用户 <strong>{selectedUser.nickname}</strong> 的账号。
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">注销后果：</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>用户无法再登录系统</li>
                      <li>历史答题和获奖记录保留</li>
                      <li>用户数据不可再更新</li>
                      <li>可通过恢复功能撤销此操作</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="form-label">注销原因 *</label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className={`form-textarea ${deleteReason.trim().length === 0 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                rows={3}
                placeholder="请输入注销该用户的原因..."
                required
                minLength={5}
                maxLength={200}
              />
              <div className="mt-1 flex justify-between text-sm">
                <span className={`${deleteReason.trim().length === 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  {deleteReason.trim().length === 0 ? '请输入注销原因' : '原因描述详细'}
                </span>
                <span className="text-gray-400">
                  {deleteReason.length}/200
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteUserMutation.isPending || !validateReason(deleteReason)}
                className="btn btn-danger"
              >
                {deleteUserMutation.isPending ? '注销中...' : '确认注销'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <ArrowPathIcon className="h-6 w-6 text-green-600" />
              <h3 className="text-lg font-medium text-gray-900">恢复用户账号</h3>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                您即将恢复用户 <strong>{selectedUser.nickname}</strong> 的账号。
              </p>
              <p className="text-sm text-gray-500">
                恢复后，用户将能够重新登录和使用系统。
              </p>
            </div>
            <div className="mb-4">
              <label className="form-label">恢复原因 *</label>
              <textarea
                value={restoreReason}
                onChange={(e) => setRestoreReason(e.target.value)}
                className={`form-textarea ${restoreReason.trim().length === 0 ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                rows={3}
                placeholder="请输入恢复该用户的原因..."
                required
                minLength={5}
                maxLength={200}
              />
              <div className="mt-1 flex justify-between text-sm">
                <span className={`${restoreReason.trim().length === 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  {restoreReason.trim().length === 0 ? '请输入恢复原因' : '原因描述详细'}
                </span>
                <span className="text-gray-400">
                  {restoreReason.length}/200
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="btn btn-secondary"
              >
                取消
              </button>
              <button
                onClick={confirmRestore}
                disabled={restoreUserMutation.isPending || !validateReason(restoreReason)}
                className="btn btn-primary"
              >
                {restoreUserMutation.isPending ? '恢复中...' : '确认恢复'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}